import { ofType } from 'redux-observable';
import { of, concat, EMPTY } from 'rxjs';
import {
    mergeMap,
    exhaustMap,
    switchMap,
    withLatestFrom,
    map,
    delay,
} from 'rxjs/operators';

import {
    ERROR_FORBIDDEN,
    ERROR_CONFLICT,
} from '@perpay-web/constants/httpStatusCodes';
import { analyticsIdentifyUser } from '@perpay-web/fintech/actions/analytics/userInfo';
import {
    replaceAuthTokens,
    refreshAccessToken,
    partnerOnboardingRedirectExistingUser,
} from '@perpay-web/fintech/actions/authentication';
import {
    partnerOnboardingSignup as partnerOnboardingSignupAction,
    partnerOnboardingSignupSuccess,
    partnerOnboardingSignupError,
    verifyPartnerOnboardingCode as verifyPartnerOnboardingCodeAction,
    verifyPartnerOnboardingCodeSuccess,
    partnerOnboardingSubmitCardApplication as partnerOnboardingSubmitCardApplicationAction,
    partnerOnboardingSubmitCardApplicationSuccess,
    partnerOnboardingSubmitCardApplicationError,
    createPassword as createPasswordAction,
    createPasswordError,
} from '@perpay-web/fintech/actions/entities/partnerOnboarding';
import { fetchPartnerHostedCardApplicationSuccess } from '@perpay-web/fintech/actions/ui/partnerHostedCardApplication';
import {
    analyticsSignupSuccess,
    analyticsOnboardingAddPhoneNumber,
    analyticsOnboardingAddCompany,
    analyticsOnboardingAddNetPay,
} from '@perpay-web/fintech/actions/ui/signup';
import { APPLICATION_STEP } from '@perpay-web/fintech/components/screens/CardApplication/CardApplicationSteps';
import { cardApplicationSetSteps } from '@perpay-web/fintech/components/screens/CardApplication/useCardApplicationContext';
import { SELF_ONBOARDED_COMPANY_NAME } from '@perpay-web/fintech/constants/companies';
import { SIGNUP_FLOW_PARTNER_ONBOARDING } from '@perpay-web/fintech/constants/signupFlowTypes';
import {
    VERIFY_PARTNER_ONBOARDING_CODE_ENDPOINT,
    PARTNER_ONBOARDING_SUBMIT_CARD_APPLICATION_ENDPOINT,
    SET_PASSWORD_ENDPOINT,
} from '@perpay-web/fintech/constants/urls';
import { cardApplicationConfirmAddressModalOpen } from '@perpay-web/fintech/hooks/useCardApplicationConfirmAddressModalContext';
import {
    partnerOnboardingExistingUserModalSetExistingUserFirstName,
    partnerOnboardingExistingUserModalSetIsModalOpen,
    partnerOnboardingExistingUserModalSetMatchMethod,
} from '@perpay-web/fintech/hooks/usePartnerOnboardingExistingUserModalContext';
import { partnerOnboardingSignupIneligibleStateModalSetIsModalOpen } from '@perpay-web/fintech/hooks/usePartnerOnboardingSignupIneligibleStateModalContext';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { getUserInfo } from '@perpay-web/fintech/selectors/entities/userInfo';
import { authentication } from '@perpay-web/fintech/settings/singletons';
import { installSegmentMiddleware } from '@perpay-web/fintech/utils/analyticsUtils';
import { getPartnerNameFromCode } from '@perpay-web/fintech/utils/partnerOnboardingUtils';
import { handleError } from '@perpay-web/observable/operators/handleError';
import { handleErrorMessageWithFallback } from '@perpay-web/observable/operators/handleErrorMessageWithFallback';
import { replace } from '@perpay-web/services/router';
import { standardizeError } from '@perpay-web/utils/errorHandlers';
import { getAnalyticsIdentity } from '@perpay-web/utils/tokenUtils';

export const partnerOnboardingSignup = (action$, state$) =>
    action$.pipe(
        ofType(partnerOnboardingSignupAction().type),
        exhaustMap((action) =>
            authentication
                .partnerHostedSignup(action.payload)
                .then((results) =>
                    installSegmentMiddleware().then(() => results),
                ),
        ),
        withLatestFrom(state$),
        mergeMap(([results, state]) => {
            const { tokens: unusedTokens, ...rest } = results;

            const decodedTokens = authentication.getDecodedTokens();
            const identityAction = getAnalyticsIdentity(
                decodedTokens,
                getUserInfo(state),
            );
            const partnerCode = authentication.getPartnerOnboardedCode();
            const partnerName = getPartnerNameFromCode(partnerCode);
            const uuid = authentication.getUserUuid();
            replace(
                `${paths.partnerOnboardingApplication.path}/${window.location.search}`,
            );
            return [
                analyticsIdentifyUser(identityAction),
                replaceAuthTokens(decodedTokens),
                partnerOnboardingSignupSuccess(rest),
                analyticsSignupSuccess({
                    ...rest,
                    uuid,
                    signupFlow: SIGNUP_FLOW_PARTNER_ONBOARDING,
                    partner: partnerName,
                }),
                analyticsOnboardingAddPhoneNumber({
                    signupFlow: SIGNUP_FLOW_PARTNER_ONBOARDING,
                    partner: partnerName,
                }),
                cardApplicationSetSteps(APPLICATION_STEP),
            ];
        }),
        handleError((error) => {
            const returnActions = [partnerOnboardingSignupError(error)];

            if (error.status === ERROR_CONFLICT) {
                const {
                    message,
                    name: firstName,
                    method,
                    mfaRequired,
                } = error.response;
                const isMfaRequired = mfaRequired ? mfaRequired[0] : false;

                returnActions.push(partnerOnboardingRedirectExistingUser());

                if (
                    (method && method === 'phone') ||
                    message[0].includes('Phone') ||
                    isMfaRequired
                ) {
                    replace(paths.login.path);
                    return returnActions;
                }
                returnActions.push(
                    partnerOnboardingExistingUserModalSetIsModalOpen(true),
                );
                if (firstName) {
                    returnActions.push(
                        partnerOnboardingExistingUserModalSetExistingUserFirstName(
                            firstName,
                        ),
                    );
                }
                if (method) {
                    returnActions.push(
                        partnerOnboardingExistingUserModalSetMatchMethod(
                            method,
                        ),
                    );
                }
            } else if (error.status === ERROR_FORBIDDEN) {
                returnActions.push(
                    partnerOnboardingSignupIneligibleStateModalSetIsModalOpen(
                        true,
                    ),
                );
            }

            const { message } = error.response;
            if (message[0] === 'Invalid address, more information is needed') {
                returnActions.push(
                    cardApplicationConfirmAddressModalOpen(true),
                );
            }
            return returnActions;
        }),
    );

export const verifyPartnerOnboardingCode = (action$, state$, { jsonFetch }) =>
    action$.pipe(
        ofType(verifyPartnerOnboardingCodeAction().type),
        switchMap((action) => {
            const { partnerCode, onboardingProduct, offerCode, productName } =
                action.payload;
            let endpoint = `${VERIFY_PARTNER_ONBOARDING_CODE_ENDPOINT}?partner_code=${partnerCode}&onboarding_product=${onboardingProduct}`;
            if (offerCode) {
                endpoint += `&offer_code=${offerCode}`;
            }
            if (productName) {
                endpoint += `&product_name=${productName}`;
            }

            return jsonFetch(endpoint).then((results) => [
                results,
                partnerCode,
                offerCode,
            ]);
        }),
        mergeMap(([results, partnerCode, offerCode]) => {
            const isVerified = results.verified_code;
            if (!isVerified) {
                replace(paths.signUp.path);
                return [];
            }

            const payload = {
                partnerCode,
                offerCode: results.verified_offer ? offerCode : null,
                offerAmount: results.offer_amount ? results.offer_amount : null,
                premiumCardApplicant: results.premium_card_applicant,
            };

            return [verifyPartnerOnboardingCodeSuccess(payload)];
        }),
        handleError(() => {
            replace(paths.signUp.path);
        }),
    );

export const partnerOnboardingSubmitCardApplication = (
    action$,
    state$,
    { post },
) =>
    action$.pipe(
        ofType(partnerOnboardingSubmitCardApplicationAction().type),
        switchMap((action) =>
            post(
                PARTNER_ONBOARDING_SUBMIT_CARD_APPLICATION_ENDPOINT,
                action.payload,
            ).pipe(
                map((results) => {
                    const isSelfOnboarded = Object.keys(
                        action.payload.jobInfo.selfOnboardingData,
                    ).length;
                    const companyName = isSelfOnboarded
                        ? SELF_ONBOARDED_COMPANY_NAME
                        : action.payload.jobInfo.company.company;
                    return [results, companyName];
                }),
            ),
        ),
        mergeMap(([results, companyName]) => {
            const cardAccountApplication = results.response;

            const partnerCode = authentication.getPartnerOnboardedCode();
            const partner = getPartnerNameFromCode(partnerCode);

            // First dispatch data storage actions immediately
            const dataActions$ = of([
                partnerOnboardingSubmitCardApplicationSuccess(
                    cardAccountApplication,
                ),
                fetchPartnerHostedCardApplicationSuccess(
                    cardAccountApplication,
                ),
            ]);

            // Then dispatch navigation actions with a small delay to ensure data is stored
            const navigationActions$ = of([
                analyticsOnboardingAddCompany({
                    companyName,
                    signupFlow: SIGNUP_FLOW_PARTNER_ONBOARDING,
                    partner,
                }),
                analyticsOnboardingAddNetPay({
                    signupFlow: SIGNUP_FLOW_PARTNER_ONBOARDING,
                    partner,
                }),
            ]).pipe(delay(10)); // Small delay to ensure data actions are processed first

            return concat(dataActions$, navigationActions$);
        }),
        // Flatten the nested arrays from concat
        mergeMap((actions) => actions),
        handleError((error) => {
            if (error.status >= 500) {
                return EMPTY;
            }
            const standardizedError = standardizeError(error);
            return [
                partnerOnboardingSubmitCardApplicationError(standardizedError),
            ];
        }),
    );

export const createPassword = (action$, state$, { post }) =>
    action$.pipe(
        ofType(createPasswordAction.type),
        exhaustMap((action) => {
            const payload = {
                password: action.payload.password,
            };

            return post(SET_PASSWORD_ENDPOINT, payload).pipe(
                map((results) => [results, action]),
            );
        }),
        mergeMap(([, action]) => {
            if (action.payload.dashboardRedirect) {
                replace(`${paths.dashboard.path}?PHO_complete=true`);
            }

            return [refreshAccessToken()];
        }),
        handleErrorMessageWithFallback((error) => [createPasswordError(error)]),
    );
